import type {
  DataFetchParams,
  DataFetchResponse,
  DynamicFilterOptionsPage,
  FilterOptionsParams,
  ResponseMeta,
} from '@/components/DataTable';
import {
  getOrganizationDevice,
  getOrganizationDeviceFilterOptions,
  getOrganizationDevicePosture,
  linkOrganizationDevicePerson,
  listOrganizationDevices,
  resetOrganizationDevice,
  revokeOrganizationDevice,
} from '@kirin/api-client/api/device-inventory';
import {
  DEVICE_FILTERABLE_FIELDS,
  type DeviceDetail,
  type DeviceFilterOptionField,
  type DeviceListItem,
  type DevicePosture,
  type DeviceRevocationResponse,
  type DeviceSortField,
  type LinkDevicePersonResponse,
} from '@kirin/api-client/contracts/device-inventory';
import { FilterOperators } from '@kirin/api-client/contracts/table';
import { apiClient } from '@/lib/api-client';

/** The list response's meta plus its latest agent version (null when unknown). */
export type DeviceTableMeta = ResponseMeta & {
  latestAgentVersion: string | null;
};

/** One page of the Endpoints tab, in the DataTable's shape. */
export async function fetchDevicesDataTable(
  organizationId: string,
  params: DataFetchParams,
  signal?: AbortSignal
): Promise<DataFetchResponse<DeviceListItem, DeviceTableMeta>> {
  const response = await listOrganizationDevices(
    apiClient,
    organizationId,
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      filters: params.filters,
      sortBy: params.sort?.field as DeviceSortField | undefined,
      sortOrder: params.sort?.order,
      search: params.search,
    },
    { signal }
  );
  return {
    data: response.data,
    pagination: response.pagination,
    meta: {
      activeFilters: response.meta?.activeFilters ?? [],
      search: response.meta?.search,
      latestAgentVersion: response.latestAgentVersion,
    },
  };
}

export function fetchDevicePosture(
  organizationId: string
): Promise<DevicePosture> {
  return getOrganizationDevicePosture(apiClient, organizationId);
}

export function fetchDevice(
  organizationId: string,
  deviceId: string
): Promise<DeviceDetail> {
  return getOrganizationDevice(apiClient, organizationId, deviceId);
}

/**
 * Paged distinct endpoint names / OS families / agent versions for a column
 * filter. Names are distinct across pages and match the name or an ID only.
 */
export async function fetchDeviceFilterOptions(
  organizationId: string,
  field: DeviceFilterOptionField,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await getOrganizationDeviceFilterOptions(
    apiClient,
    organizationId,
    {
      field,
      search: params.search,
      limit: params.limit,
      offset: params.offset,
    }
  );
  const offset = params.offset ?? 0;
  const hasMore =
    response.options.length > 0 &&
    offset + response.options.length < response.total;
  return {
    options: response.options,
    total: response.total,
    hasMore,
    nextOffset: hasMore ? offset + response.options.length : null,
  };
}

/**
 * The device an endpoint ID (`machine_id`) belongs to, or null. A person's
 * endpoint links (Users → Endpoints dialog) resolve through this.
 */
export async function fetchDeviceIdForEndpointId(
  organizationId: string,
  machineId: string
): Promise<string | null> {
  const response = await listOrganizationDevices(apiClient, organizationId, {
    page: 1,
    limit: 5,
    filters: [
      {
        field: DEVICE_FILTERABLE_FIELDS.ENDPOINT,
        operator: FilterOperators.INCLUDES,
        value: [machineId],
      },
    ],
  });
  // INCLUDES also matches an exact name; prefer the machine that owns the ID.
  const owner =
    response.data.find(device => device.machineIds.includes(machineId)) ?? null;
  return owner?.id ?? null;
}

/** KIR-5413: link an Unattributed person on an endpoint to a member. */
export function linkDevicePerson(
  organizationId: string,
  userId: string,
  targetUserId: string
): Promise<LinkDevicePersonResponse> {
  return linkOrganizationDevicePerson(apiClient, organizationId, userId, {
    targetUserId,
  });
}

/** KIR-5658: revoke an enrolled device (its keys stop working). */
export function revokeDevice(
  organizationId: string,
  deviceId: string
): Promise<DeviceRevocationResponse> {
  return revokeOrganizationDevice(apiClient, organizationId, deviceId);
}

/** KIR-5658: clear a device's revoke so it can enroll again. */
export function resetDevice(
  organizationId: string,
  deviceId: string
): Promise<DeviceRevocationResponse> {
  return resetOrganizationDevice(apiClient, organizationId, deviceId);
}
